import { FC, useEffect, useState } from "react";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { ProviderInvoiceService } from "../../../services/providerInvoices/providerInvoiceService";
import { toast } from "sonner";
import * as yup from 'yup';
import { useFormik } from "formik";
import { Modal, Button, Tooltip, ActionIcon } from "rizzui";
import { DatePicker } from "../../../components/forms/DatePicker";
import CustomTable from "../../../components/table/CustomTable";
import moment from "moment";
import { es } from "date-fns/locale";
import RequiredInput from "../../../components/forms/InputHOC";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import CustomSelect from "../../../components/forms/CustomSelect";
import { HiOutlineDownload } from "react-icons/hi";
import cn from "../../../utils/classNames";

interface ModalAutoInvoicesProps {
    isOpen: boolean;
    onClose: () => void;
    setDataInvoicesAutomatic: (data: any) => void;
    dataInvoicesAutomatic: any;
    loadingInvoicesAutomatic: boolean;
    setLoadingInvoicesAutomatic: (loading: boolean) => void;
    confirmInvoicing: boolean;
    setConfirmInvoicing: (confirm: boolean) => void;

};

const columnOrder = [
    'number',
    'providerName',
    'providerEmail',
    'providerCif',
    'invoiceDate',
    'paymentDate',
    'totalAmount',
    'ivaTax',
    'isPaid'
];

const schema = yup.object().shape({
    dateFrom: yup.date().required('Selecciona la fecha de inicio del rango'),
    dateTo: yup
        .date()
        .required('Selecciona la fecha de fin del rango')
        .min(yup.ref('dateFrom'), 'La fecha de fin no puede ser anterior a la de inicio'),
    invoiceCount: yup
        .number()
        .typeError('Indica un número de facturas')
        .min(1, 'Mínimo 1 factura')
        .max(10000, 'Máximo 10000 facturas')
        .required('Indica el número de facturas'),
});

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const ModalAutoInvoices: FC<ModalAutoInvoicesProps> = ({ isOpen, onClose, setDataInvoicesAutomatic, dataInvoicesAutomatic, loadingInvoicesAutomatic, setLoadingInvoicesAutomatic, confirmInvoicing, setConfirmInvoicing }) => {

    // STATES

    const [data, setData] = useState<any>([]);
    const [amountSurplus, setAmountSurplus] = useState<number>(0);
    const [runInfo, setRunInfo] = useState<{
        requested: number;
        created: number;
        capped: boolean;
        feasibleMax: number;
    } | null>(null);
    const [isSearched, setIsSearched] = useState(false);
    
    // HOOKS

    const { handleErrors } = useHandleErrors();
    const service = new ProviderInvoiceService();
    const { userCan } = usePrivilege();
    const { getCompaniesList } = useCompanies();

    // FUNCTIOS

    // -------------------------------------------------------------------------------------------------
    /**
     * @EN ORDER AND STRUCTURE DATA FOR TABLE DISPLAY
     * @ES ORDENAR Y ESTRUCTURAR DATOS PARA MOSTRAR EN LA TABLA
     */
    // -------------------------------------------------------------------------------------------------
    const ordered_data = (responseData: any) => {
        // Manejar la nueva estructura de respuesta
        const invoicesCreated = responseData?.invoicesCreated?.data || responseData || [];
        const surplus = responseData?.amountSurplus || 0;
        setRunInfo(
            responseData?.requestedCount != null
                ? {
                      requested: responseData.requestedCount,
                      created: responseData.createdCount ?? invoicesCreated.length,
                      capped: Boolean(responseData.capped),
                      feasibleMax: responseData.feasibleMax ?? 0,
                  }
                : null
        );
        setAmountSurplus(surplus);
        
        if (!invoicesCreated || !Array.isArray(invoicesCreated)) {
            return [];
        }

        return invoicesCreated.map((invoice: any, index: number) => ({
            id: invoice.id || index,
            number: invoice.number || `AUTO-${String(index + 1).padStart(4, '0')}`,
            providerName: invoice.providerName || 'N/A',
            providerEmail: invoice.providerEmail || 'N/A',
            providerCif: invoice.providerCif || 'N/A',
            invoiceDate: invoice.invoiceDate || invoice.date || new Date().toISOString(),
            paymentDate: invoice.paymentDate || invoice.dueDate || null,
            totalAmount: parseFloat(invoice.totalAmount || invoice.amount || 0),
            ivaTax: parseFloat(invoice.ivaTax || invoice.tax || 21),
            isPaid: Boolean(invoice.isPaid || invoice.paid || false),
            comments: invoice.comments || ''
        })).sort((a: any, b: any) => {
            // Ordenar por fecha de factura (más reciente primero)
            const dateA = new Date(a.invoiceDate).getTime();
            const dateB = new Date(b.invoiceDate).getTime();
            return dateB - dateA;
        });
    }
    // -------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------
    /**
     * @EN GENERATE AUTO INVOICES WITH PARAM SELECTED
     * @ES GENERAR FACTURAS AUTOMATICAS CON LOS PARAMETROS SELECCIONADOS
     * 
     * 
     */
    // -------------------------------------------------------------------------------------------------
    const generateAutoInvoices = async (data: any) => {
        setConfirmInvoicing(false)
        try {
            const response = (await service.generateAutomaticInvoices(data)).getResponseData();
            if (response.success) {
                toast.success(response.message);
                const processedData = ordered_data(response.data);
                setData(processedData);
                setConfirmInvoicing(true)
                formik.resetForm();
            } else {
                toast.error(response.message);
                setConfirmInvoicing(false)
                formik.resetForm();
            }
        } catch (error: any) {
            toast.error(error.message);
            setConfirmInvoicing(false)

        }
    }
    // -------------------------------------------------------------------------------------------------

      // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGAR FACTURAS PROVENIENTE BLOB EN FORMATO ZIP
     * @EN DOWNLOAD INVOICES FROM BLOB IN ZIP FORMAT
     */
    // -------------------------------------------------------------------------------------------------------------
    const downloadMulti = async (ids: string[]) => {
        try {
            const response = await service.downloadMultiProviderInvoice(ids);
            
            if (response && response.getResponseData()) {
                const fileData = response.getResponseData();
                
                // Verificar que los datos no estén vacíos
                if (fileData) {
                    // Crear un blob con el tipo MIME para archivos ZIP
                    const blob = new Blob([fileData], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    
                    // Generar nombre del archivo con timestamp
                    const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
                    const fileName = `facturas_proveedores_${timestamp}.zip`;
                    link.setAttribute('download', fileName);
                    
                    // Descargar el archivo
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    toast.success('Facturas descargadas correctamente en formato ZIP');
                } else {
                    toast.error('No se pudieron obtener los archivos de facturas');
                }
            } else {
                toast.error('Error al descargar las facturas');
            }
        } catch (error: any) {
            toast.error(`Error al descargar las facturas: ${error.message || 'Error desconocido'}`);
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------


     // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGAR UNA FACTURA PROVEEDOR
     * @EN DOWNLOAD A PROVIDER INVOICE
     * 
     * @param id 
     * @returns 
     */
    // -------------------------------------------------------------------------------------------------------------
    const downloadInvoice = async (id: string, name: string) => {
        try {
            const response = await service.downloadProviderInvoice(id);
            if (response && response.getResponseData()) {
                const fileData = response.getResponseData();
                if (fileData) {
                    const blob = new Blob([fileData], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    const fileName = `factura_proveedor_${name}.pdf`;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    toast.success('Factura descargada correctamente en formato PDF');
                } else {
                    toast.error('No se pudieron obtener los archivos de facturas');
                }
            } else {
                toast.error('Error al descargar las facturas');
            }
        } catch (error: any) {
            toast.error(`Error al descargar las facturas: ${error.message || 'Error desconocido'}`);
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------
    /**
     * @EN FORMIK FOR AUTO INVOICES
     * @ES FORMIK PARA FACTURAS AUTOMATICAS
     */
    // -------------------------------------------------------------------------------------------------
    const formik = useFormik({
        initialValues: {
            dateFrom: null as Date | null,
            dateTo: null as Date | null,
            invoiceCount: 1,
            company: null as string | null,
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        onSubmit: async (values) => {
            setLoadingInvoicesAutomatic(true);
            const formattedValues = {
                company: values.company,
                dateFrom: values.dateFrom ? moment(values.dateFrom).format('YYYY-MM-DD') : null,
                dateTo: values.dateTo ? moment(values.dateTo).format('YYYY-MM-DD') : null,
                invoiceCount: Number(values.invoiceCount),
            };
            await generateAutoInvoices(formattedValues);
            setLoadingInvoicesAutomatic(false);
        },
    });
    // -------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        formik.resetForm();
        setRunInfo(null);
    }, [isOpen]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label === 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            overlayClassName="backdrop-blur"
            containerClassName='!shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-8'>
                <div className='mb-7 flex items-center justify-between'>
                    <h2 className="text-xl font-semibold">Generar facturas automáticas</h2>
                </div>
                
                <div className="mb-6">
                    {!confirmInvoicing ? (
                        <form onSubmit={formik.handleSubmit} className="space-y-4">
                            <p className="text-sm text-gray-600">
                                Se generan N facturas con importe al azar (base sin IVA, inferior a 1.000€) repartiendo al azar entre
                                los proveedores activos con CIF: como máximo 3 facturas por CIF en este envío. Cada factura toma una
                                fecha al azar dentro del rango (en días laborables, si el rango los incluye).
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Rango de fechas (emisión) <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <DatePicker
                                            monthsShown={1}
                                            placeholderText="Desde"
                                            inputProps={{
                                                inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.touched.dateFrom && formik.errors.dateFrom ? 'border-red-500' : ''}`,
                                            }}
                                            className="w-full"
                                            selected={formik.values.dateFrom}
                                            onChange={(d: any) => formik.setFieldValue('dateFrom', d)}
                                            onBlur={() => formik.setFieldTouched('dateFrom', true)}
                                            locale={es}
                                        />
                                    </div>
                                    <div>
                                        <DatePicker
                                            monthsShown={1}
                                            placeholderText="Hasta"
                                            inputProps={{
                                                inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.touched.dateTo && formik.errors.dateTo ? 'border-red-500' : ''}`,
                                            }}
                                            className="w-full"
                                            selected={formik.values.dateTo}
                                            onChange={(d: any) => formik.setFieldValue('dateTo', d)}
                                            onBlur={() => formik.setFieldTouched('dateTo', true)}
                                            locale={es}
                                            minDate={formik.values.dateFrom || undefined}
                                        />
                                    </div>
                                </div>
                                {(formik.touched.dateFrom && formik.errors.dateFrom) && (
                                    <p className="text-red-500 text-sm mt-1">{formik.errors.dateFrom.toString()}</p>
                                )}
                                {(formik.touched.dateTo && formik.errors.dateTo) && (
                                    <p className="text-red-500 text-sm mt-1">{formik.errors.dateTo.toString()}</p>
                                )}
                            </div>

                            <div>
                                <RequiredInput
                                    type="number"
                                    id="invoiceCount"
                                    label="Número de facturas a generar"
                                    step="1"
                                    min={1}
                                    max={10000}
                                    value={formik.values.invoiceCount}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.invoiceCount && formik.errors.invoiceCount ? String(formik.errors.invoiceCount) : ''}
                                    className="w-full"
                                />
                            </div>

                            {userCan('list', 'company') && (
                                <CustomSelect
                                required
                                isSearchable
                                id={'company'}
                                label="Compañia"
                                containerClassName='md:col-span-12'
                                value={
                                    formik.values.company ?
                                    { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                    getCompaniesList()?.find((g: any) => g.label === 'GESTION DE RESIDUOS 2023 SLU')
                                }
                                options={getCompaniesList()}
                                onChange={
                                    (e: any) => { formik.setFieldValue('company', e?.value) }
                                }
                                error={formik.errors.company}
                                />
                            )}

                        </form>
                    ) : (
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Facturas generadas automáticamente</h3>
                            
                            {runInfo?.capped && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                    <p className="text-sm text-amber-900">
                                        Se solicitaron {runInfo.requested} facturas; se generaron {runInfo.created}. El límite es
                                        3 facturas por CIF en este lote (máximo {runInfo.feasibleMax} con los CIF actuales).
                                    </p>
                                </div>
                            )}
                            {amountSurplus > 0 && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                    <div className="ml-3">
                                        <h4 className="text-sm font-medium text-green-800">
                                            Importe sobrante: €{amountSurplus.toFixed(2)}
                                        </h4>
                                    </div>
                                </div>
                            )}
                            
                            {/* Tabla de facturas generadas usando CustomTable */}
                            <CustomTable
                                id={'modal-auto-invoices-table'}
                                columnOrder={columnOrder}
                                data={data || []}
                                isLoading={false}
                                isSortable={false}
                                columnsNoSortables={[]}
                                showToggleButton={false}
                                columnsNotShown={['providerEmail', 'providerCif']}
                                overrideColumns={[
                                    {
                                        key: 'number',
                                        columnNameKey: 'Número',
                                        render: (row: any) => {
                                            return row.row.original.number ?? 'N/A'
                                        }
                                    },
                                    {
                                        key: 'providerName',
                                        columnNameKey: 'Proveedor',
                                        render: (row: any) => {
                                            return (
                                                <div className="flex flex-col">
                                                    <div className="font-bold">{row.row.original.providerName}</div>
                                                    <div className="text-sm text-gray-500 text-nowrap"><span className="font-bold">Email: </span>{row.row.original?.providerEmail ?? 'N/A'}</div>
                                                    <div className="text-sm text-gray-500 text-nowrap"><span className="font-bold">CIF: </span>{row.row.original?.providerCif ?? 'N/A'}</div>
                                                </div>
                                            )
                                        }
                                    },
                                    {
                                        key: 'invoiceDate',
                                        columnNameKey: 'Fecha Factura',
                                        render: (row: any) => {
                                            return row.row.original.invoiceDate ? moment(row.row.original.invoiceDate).format('DD/MM/YYYY') : 'N/A'
                                        }
                                    },
                                    {
                                        key: 'paymentDate',
                                        columnNameKey: 'Fecha Pago',
                                        render: (row: any) => {
                                            return row.row.original.paymentDate ? moment(row.row.original.paymentDate).format('DD/MM/YYYY') : 'N/A'
                                        }
                                    },
                                    {
                                        key: 'totalAmount',
                                        columnNameKey: 'Total sin IVA',
                                        render: (row: any) => {
                                            // totalAmount ya representa el importe sin IVA
                                            return row.row.original.totalAmount ? `${row.row.original.totalAmount.toFixed(2)} €` : 'N/A'
                                        }
                                    },
                                    {
                                        key: 'ivaTax',
                                        columnNameKey: 'Total con IVA',
                                        render: (row: any) => {
                                            // Cálculo correcto: totalAmount * (1 + ivaTax/100)
                                            const amountWithTax = row.row.original.totalAmount * (1 + row.row.original.ivaTax / 100);
                                            return `${amountWithTax.toFixed(2)} €`;
                                        }
                                    },
                                    {
                                        key: 'isPaid',
                                        columnNameKey: 'Pagada',
                                        render: (row: any) => {
                                            return (
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    row.row.original.isPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {row.row.original.isPaid ? 'Sí' : 'No'}
                                                </span>
                                            )
                                        }
                                    }
                                ]}
                                actions={[
                                    {
                                        label: "download",
                                        permissions: {
                                            group: 'provider_invoicing',
                                            permission: 'download'
                                        },
                                        render: (row: any) => {
                                            return (
                                                <Tooltip
                                                    size="sm"
                                                    content={'Descargar'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <ActionIcon
                                                        onClick={() => downloadInvoice(row.id, row.number)}
                                                        as="span"
                                                        size="sm"
                                                        variant="outline"
                                                        className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                                    >
                                                        <HiOutlineDownload className="h-4 w-4" size={20} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )
                                        }
                                    },
                                ]}
                                filters={{ limit: 100000, page: 1 }}
                                updateFilters={() => {}}
                                updateFilterOrder={() => { }}
                                multiActions={[
                                    {
                                        label: 'Eliminar facturas NO pagadas',
                                        permissions: {
                                            group: 'provider_invoicing',
                                            permission: 'download'
                                        },
                                        render: (row: any) => {
                                            return (
                                                <Tooltip
                                                    size="md"
                                                    content={'Descargar'}
                                                    placement="top"
                                                >
                                                    <ActionIcon
                                                        size="lg"
                                                        color="primary"
                                                        variant="outline"
                                                        aria-label={'Descargar facturas'}
                                                        className={cn(['cursor-pointer hover:!border-orange-600 hover:text-orange-600 border-orange-500 text-orange-500'])}
                                                        onClick={() => {
                                                            downloadMulti(row);
                                                        }}
                                                    >
                                                        <HiOutlineDownload className="h-6 w-6" />
                                                    </ActionIcon>
                                                </Tooltip>
                                            );
                                        },
                                    },
                                ]}
                                paginationData={{
                                    pageSize: data?.length || 0,
                                    currentPage: 1,
                                    pageCount: 1,
                                    totalCount: data?.length || 0,
                                    handlePagination: () => {},
                                    handlePerPage: () => {},
                                }}
                                pagination={false}
                                search={false}

                            />
                        </div>
                    )}
                </div>
                
                <div className='flex gap-4 justify-end'>
                    <Button 
                        variant="outline"
                        onClick={onClose}
                    >
                        {confirmInvoicing ? 'Cerrar' : 'Cancelar'}
                    </Button>
                    
                    {!confirmInvoicing && (
                        <Button 
                            type="submit" 
                            onClick={() => formik.handleSubmit()}
                            disabled={loadingInvoicesAutomatic || !formik.isValid}
                            isLoading={loadingInvoicesAutomatic}
                        >
                            {loadingInvoicesAutomatic ? 'Generando...' : 'Generar Facturas'}
                        </Button>
                    )}
                    
                    {confirmInvoicing && (
                        <Button 
                            onClick={() => {
                                setConfirmInvoicing(false);
                                formik.resetForm();
                                setData([]);
                                setRunInfo(null);
                            }}
                        >
                            Generar Nuevas Facturas
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    )
}

export default ModalAutoInvoices;